import {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useState,
} from 'react';

import type { CustomAttribute } from '@/types';

import { cn } from '../../utils/cn';
import useDebounce from '../../utils/useDebounce';

import MagnifyingGlass from '../../icon/MagnifyingGlass';
import XCircle from '../../icon/XCircle';
import { inputContainer, inputIconFill, inputStyle } from '../Post.css';

const ALIGNMENT = {
  center: 'justify-center',
  left: 'justify-start',
  right: 'justify-end',
};

// The number of milliseconds to wait before
// `onChange` method is invoked with new query
const QUERY_LOOKUP_DELAY_MS = 500;

interface Props {
  alignment?: keyof typeof ALIGNMENT;
  isVisible: boolean;
  onChange?: (query: string) => void;
  setIsSearching?: (isSearching: boolean) => void;
  handleNonDebouncedSearchQuery?: () => void;
  handleResetSearchState?: () => void;
  customAttributes?: CustomAttribute;
}

const SearchInput = ({
  alignment = 'center',
  onChange,
  isVisible,
  customAttributes,
  handleNonDebouncedSearchQuery,
  handleResetSearchState,
}: Props) => {
  if (!isVisible) {
    return null;
  }

  const [query, setQuery] = useState('');

  const handleClickClear: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
    onChange?.('');
    setQuery('');
    handleResetSearchState?.();
  }, []);

  const debouncedOnChange = useDebounce((query: string) => onChange?.(query), QUERY_LOOKUP_DELAY_MS);

  const handleChangeInQuery: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const newQuery = e.currentTarget.value;
    setQuery(newQuery);
    debouncedOnChange(newQuery.trim());
  }, []);

  const handleKeyDownInQuery: KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentQuery = e.currentTarget.value.trim();
      setQuery(currentQuery);
      onChange?.(currentQuery);
    }
  }, []);

  return (
    <div className={cn('flex items-center w-full', ALIGNMENT[alignment])} {...customAttributes}>
      <div className={cn('relative flex items-center w-full', inputContainer)}>
        <MagnifyingGlass className={cn('absolute left-3 h-4 w-4', inputIconFill)} />
        <input
          name="q"
          type="text"
          placeholder="Search"
          className={cn('w-full border p-4 pl-9', inputStyle)}
          onChange={(e) => {
            handleNonDebouncedSearchQuery?.();
            handleChangeInQuery(e);
          }}
          onKeyDown={handleKeyDownInQuery}
          value={query}
          style={{
            borderRadius: 'var(--input-border-radius)',
            borderWidth: 'var(--input-border-width)',
            borderColor: 'var(--input-border-color)',
            borderStyle: 'var(--input-border-style)',
          }}
        />
        {!!query && (
          <button type="button" onClick={handleClickClear} className="absolute right-3">
            <XCircle className={cn('h-4 w-4', inputIconFill)} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchInput;
